import React from "react";
import Dispatcher from "../dispatcher";
import { toastUtils } from "../utils/toast-utils";
import { Button } from "@vitalsource/vstui";
import { createThemedComponent } from "@vitalsource/vstui/themes";
import { CheckmarkCircle } from "@vitalsource/vstui-icons";
import styled, { css } from "styled-components";
import { cloud, grass } from "@vitalsource/vstui/colors";
import { isIE11 } from "../ui/utils/browser";
import moment from "moment";
import { getStoreProductRenewalUrl } from "../redux/brand/selectors";
import { store } from "../redux/store";

const ToastButton = createThemedComponent(Button, {
  Button_paddingHorizontal_medium: 0,
  Button_fontSize_medium: 20
});
const ToastHeading = styled.h3`
  padding: 5px 10px 0 10px;
  white-space: normal;
  color: ${cloud.getShade(10)};
`;
const ToastBodyText = styled.p`
  padding: 5px 0 0 10px;
  margin: 0;
`;
export const ToastLink = styled(ToastButton)`
  margin: 5px 0 0 10px;
  display: inline-block;
`;
const ToastParent = styled.div`
  color: ${cloud.getShade(10)};
  font-size: "16px";
  line-height: "24px";
`;
const ToastMessage = styled.div`
  padding-bottom: 10px;
  ${p =>
    p.bold &&
    css`
      font-weight: 500;
    `};
`;
const FavoriteToast = styled.div`
  display: flex;
`;

const LibraryHelpers = {
  exitLibraryCloseToast() {
    this.closeUpdateBookToast();
    toastUtils.dismiss(`borrow-book-toast`);
    toastUtils.dismiss(`book-expiring-toast`);
    toastUtils.dismiss(`book-has-expired-toast`);
    toastUtils.dismiss(`book-returned-toast`);
    toastUtils.dismiss(`book-favorites-toast`);
  },

  closeUpdateBookToast() {
    const updateToast =
      Fraction.Helpers.Users.getUserData("updateToastObject") || {};
    updateToast.hideMessage = true;
    Fraction.Helpers.Users.saveUserData("updateToastObject", updateToast);
    toastUtils.dismiss(`book-update-toast`);
  },

  dismissSupportedBrowserToast(toastID) {
    Fraction.Helpers.saveUserData(toastID, Fraction.User.sessionId);
    toastUtils.dismiss(toastID);
  },

  updateLibraryFilter() {
    Dispatcher.emit(Dispatcher.VIEW_UPDATED_BOOKS_SHOW);
    toastUtils.dismiss(`book-update-toast`);
  },

  renderToast(overRides = {}) {
    let toastProperties = {
      id: "book-toast",
      backgroundColor: "#FFFFFF",
      borderColor: "#757575",
      borderPosition: "left",
      maxWidth: 230,
      horizontalPosition: "right",
      dismissible: true
    };
    toastProperties = { ...toastProperties, ...overRides };
    toastUtils.notify(toastProperties);
  },

  updateBookToast(bookList = []) {
    const updateToast = Fraction.Helpers.getUserData("updateToastObject") || {};
    const updatedBooks = bookList.filter(book => {
      const todaysDate = Date.now();
      const yearMilSec = 35000000000;
      const bookExpires = Date.parse(book.expiresAt);
      const bookBuilt = Date.parse(book.builtAt);
      const bookViewed = Date.parse(book.viewedAt);

      return (
        !updateToast.hideMessage &&
        book.viewedAt !== null &&
        bookBuilt > bookViewed &&
        todaysDate - bookViewed < yearMilSec &&
        todaysDate < bookExpires
      );
    });
    if (updatedBooks.length > 0) {
      let toastContent;
      let imgCoverStyle;
      const book = updatedBooks[0];
      const coverImage = `${Fraction.Environment.coversHost}/vbid/${book.isbn}/width/60?style=preview`;
      if (updatedBooks.length > 1) {
        imgCoverStyle = "multiple";
        toastContent = (
          <span className="multiple-book-update">
            {toHTML(
              i18n.t("library.book-update-toast", {
                booksUpdated: updatedBooks.length
              })
            )}
          </span>
        );
      } else if (updatedBooks.length === 1) {
        toastContent = (
          <span className="single-book-update">
            {toHTML(
              i18n.t("library.book-update-toast2", { bookTitle: book.title })
            )}
          </span>
        );
      }
      this.renderToast({
        id: "book-update-toast",
        children: toastContent,
        imageUrl: coverImage,
        backgroundColor: "#F6F6F6",
        borderColor: "#f07d00",
        borderPosition: "left",
        maxWidth: 250,
        dismissible: true,
        imgCoverStyle: imgCoverStyle,
        onDismiss: this.closeUpdateBookToast
      });
    }
  },

  borrowBookToast(asset, launch) {
    const getURL = window.location;
    const openBookURL = `${getURL.protocol + "//" + getURL.host}/#/books/${
      asset.vbid
    }`;
    const toastContent = (
      <div className="borrow-book-toast">
        {toHTML(i18n.t("explore.add-borrow-book"))}
        <a
          href={openBookURL}
          onClick={e => {
            e.preventDefault();
            launch();
          }}
          style={{
            fontWeight: "700",
            marginTop: "5px",
            textDecoration: "underline",
            display: "block",
            color: "#007fad"
          }}
        >
          {i18n.t("library.open-book")}
        </a>
      </div>
    );
    const coverImage = `${Fraction.Environment.coversHost}/vbid/${asset.vbid}/width/60?style=preview`;
    this.renderToast({
      id: "borrow-book-toast",
      children: toastContent,
      imageUrl: coverImage,
      maxWidth: 200,
      onDismiss: this.closeUpdateBookToast
    });
  },

  returnBookToast(title, vbid) {
    let toastContent;
    toastContent = (
      <div className="borrow-book-toast">
        {i18n.t("explore.title-was-returned", { title: title })}
      </div>
    );
    toastUtils.dismiss(`borrow-book-toast`);
    toastUtils.dismiss(`book-returned-toast`);

    const coverImage = `//covers-cfw.vitalsource.com/vbid/${vbid}/width/60?style=preview`;
    this.renderToast({
      id: "book-returned-toast",
      children: toastContent,
      imageUrl: coverImage,
      maxWidth: 200,
      dismissible: true,
      onDismiss: this.closeUpdateBookToast
    });
  },

  favoriteBookToast(title) {
    const toastContent = (
      <FavoriteToast>
        <CheckmarkCircle
          style={{
            color: grass.getShade(2),
            width: "40px",
            marginRight: "8px"
          }}
        />
        {i18n.t("explore.title-is-favorite", { title: title })}
      </FavoriteToast>
    );

    this.renderToast({
      id: "book-favorites-toast",
      children: toastContent,
      type: "none",
      borderColor: grass.getShade(2),
      backgroundColor: "none",
      horizontalPosition: "right",
      dismissible: true
    });
  },

  unFavoriteBookToast(title) {
    const toastContent = (
      <FavoriteToast>
        {i18n.t("explore.title-is-unfavorite", { title: title })}
      </FavoriteToast>
    );

    this.renderToast({
      id: "book-favorites-toast",
      children: toastContent,
      type: "none",
      dismissible: true
    });
  },

  supportedBrowserToast() {
    const toastID = "supported-browser-toast";
    const userSession = Fraction.Helpers.getUserData(toastID);
    const supportURL = `${i18n.t("locations.zendesk-browser-support")}`;
    const toastHeading = (
      <ToastHeading>
        {i18n.t("supported-browser.end-date-heading")}
      </ToastHeading>
    );
    const toastContent = (
      <div className="browser-support-toast">
        <ToastBodyText>
          {i18n.t("supported-browser.supported-body-text")}
        </ToastBodyText>
        <ToastLink href={supportURL} target="_blank" naked inline>
          {i18n.t("base.learn-more")}
        </ToastLink>
      </div>
    );
    if (isIE11() && Fraction.User.sessionId !== userSession) {
      this.renderToast({
        id: toastID,
        title: toastHeading,
        children: toastContent,
        maxWidth: 250,
        type: "none",
        onDismiss: () => this.dismissSupportedBrowserToast(toastID)
      });
    }
  },

  expiringBookToast(asset) {
    const toastId = "expiring-soon-book-toast-" + asset.isbn;
    const { assetExpiresAt } = Fraction.Helpers.getUserData(toastId) || {};
    // Toasts get pulled outside the main app area, redux connect fails with no provider
    const storeUrl = getStoreProductRenewalUrl(store.getState(), asset.isbn);
    if (storeUrl && assetExpiresAt !== asset.expiresAt) {
      this.renderToast({
        id: toastId,
        title: i18n.t("recent-expired.reminder-expiring-soon"),
        children: (
          <ToastParent>
            <ToastMessage>
              {i18n.t("recent-expired.will-expire-on", {
                title: asset.title,
                date: moment(asset.expiresAt).format("l")
              })}
            </ToastMessage>
            <ToastButton
              href={`/#/redirect/${encodeURIComponent(storeUrl)}?sso=true`}
              target="_blank"
              inline
              naked
            >
              {i18n.t("recent-expired.extend-access")}
            </ToastButton>
          </ToastParent>
        ),
        imageUrl: `${Fraction.Environment.coversHost}/vbid/${asset.isbn}/width/60?style=preview`,
        onDismiss: () => this.dismissExpiringBookToast(toastId, asset)
      });
    }
  },

  dismissExpiringBookToast(toastId, asset) {
    // Storage shared with supernova
    Fraction.Helpers.saveUserData(toastId, { assetExpiresAt: asset.expiresAt });
    toastUtils.dismiss(toastId);
  },

  bookHasExpired(asset) {
    const toastId = "expired-book-toast-" + asset.isbn;
    const { assetExpiresAt } = Fraction.Helpers.getUserData(toastId) || {};
    const storeUrl = getStoreProductRenewalUrl(store.getState(), asset.isbn, {
      utm_medium: "bookshelf-library-expired",
      utm_campaign: asset.isbn
    });
    const expiredHowManyMsAgo =
      Date.now() - new Date(asset.expiresAt).valueOf();
    // Because we're using local storage, don't show the toast if it expired a long time ago
    const expiredHowManyDaysAgo = expiredHowManyMsAgo / (1000 * 60 * 60 * 24);
    if (
      storeUrl &&
      assetExpiresAt !== asset.expiresAt &&
      expiredHowManyDaysAgo < 30
    ) {
      this.renderToast({
        id: toastId,
        children: (
          <ToastParent>
            <ToastMessage bold>
              {i18n.t("library.asset-has-expired", { title: asset.title })}
            </ToastMessage>
            <ToastButton
              naked
              href={`/#/redirect/${encodeURIComponent(storeUrl)}?sso=true`}
              target="_blank"
              inline
            >
              {i18n.t("recent-expired.get-access")}
            </ToastButton>
          </ToastParent>
        ),
        imageUrl: `${Fraction.Environment.coversHost}/vbid/${asset.isbn}/width/60?style=preview`,
        onDismiss: () => this.closeBookHasExpiredToast(toastId, asset)
      });
    }
  },

  closeBookHasExpiredToast(toastId, asset) {
    Fraction.Helpers.saveUserData(toastId, { assetExpiresAt: asset.expiresAt });
    toastUtils.dismiss(toastId);
  }
};

export default LibraryHelpers;
